/**
 * Backend REST API wrapper for RTK Query.
 * Replaces firebaseApiWrapper.rtkq.ts (Firestore fakeBaseQuery).
 *
 * All data reads/writes go through the NestJS backend instead of Firestore.
 * Firebase Auth is still used for identity — the Firebase ID token is sent
 * as the Authorization header.
 */
import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { auth } from "@/lib/firebase";

const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL || "https://localhost:4001";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BACKEND_URL,
  prepareHeaders: async (headers) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      headers.set("Authorization", `Bearer ${token}`);
    }

    const officeId = localStorage.getItem("officeId");
    if (officeId) {
      headers.set("x-office-id", officeId);
    }

    headers.set("x-legalix-channel", "webapp");
    return headers;
  },
});

const isOfficeChanged = (error: FetchBaseQueryError | undefined): boolean => {
  const data = error?.data;
  if (!data || typeof data !== "object") return false;
  const body = data as { code?: unknown; message?: unknown };
  if (body.code === "OFFICE_CHANGED") return true;
  return (
    typeof body.message === "object" &&
    body.message !== null &&
    "code" in body.message &&
    (body.message as { code?: unknown }).code === "OFFICE_CHANGED"
  );
};

/** Drop a stored office the user no longer belongs to, then retry once. */
const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extra) => {
  const result = await rawBaseQuery(args, api, extra);
  if (!isOfficeChanged(result.error)) return result;
  localStorage.removeItem("officeId");
  return rawBaseQuery(args, api, extra);
};

export const backendApi = createApi({
  reducerPath: "backendApi",
  baseQuery,
  tagTypes: [
    "Conversations",
    "Documents",
    "Plans",
    "Apps",
    "Accounts",
    "Subscription",
    "Transcriptions",
    "Summaries",
    "AgentTuning",
    "Legal",
    "SpeakerIdentifications",
    "Users",
    "Members",
    "Cases",
    "CaseAssignments",
    "Invitations",
    "Dashboard",
    "Usage",
    "Notifications",
    "AccessRequests",
    "Integrations",
    "CourtScraper",
  ],
  endpoints: () => ({}),
});
